import Konva from "konva";
import { Options, useHotkeys } from "react-hotkeys-hook";
import { WallShape } from "../../../../../types";
import { raySegmentIntersection } from "../../../../../utils/geometry";
import { getAllWallSegments } from "../../../../../utils/wallPathSegments";
import { createWallPath } from "../../../../../utils/shapeFactory";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapCanvasStage } from "../../../contexts/MapCanvasContext/useMapCanvasStage";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { useMapEditorTool } from "../../../contexts/MapEditorContext";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { GLOBAL_SHORTCUTS_SCOPE } from "../shortcuts";

export const useWallExtendShortcut = (
  stageRef: React.RefObject<Konva.Stage | null>
) => {
  const { activeTool, drawTool } = useMapEditorTool();
  const { setPreviewShape, handleDrawEnd, setShapes } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { stagePositionRef } = useMapCanvasStage();
  const { shortcuts } = useEditorShortcuts();

  const enabled = (activeTool === "wall" || activeTool === "wall-path") && drawTool.startPoint !== null;

  const shortcutOptions: Options = {
    scopes: [GLOBAL_SHORTCUTS_SCOPE],
    enabled,
    preventDefault: true
  };

  const extendWall = () => {
    const stage = stageRef.current;
    if (!stage || !drawTool.startPoint) return;

    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) return;

    const stagePosition = stagePositionRef.current;
    const cursorPosition = {
      x: (pointerPos.x - stagePosition.x) / stagePosition.scale,
      y: (pointerPos.y - stagePosition.y) / stagePosition.scale
    };

    // Compute direction from startPoint to cursor
    const dx = cursorPosition.x - drawTool.startPoint.x;
    const dy = cursorPosition.y - drawTool.startPoint.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 1) return;

    // Snap direction to nearest 45-degree angle for clean straight walls
    const angle = Math.atan2(dy, dx);
    const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
    const direction = {
      x: Math.cos(snappedAngle),
      y: Math.sin(snappedAngle)
    };

    const walls = getAllWallSegments(shapes);

    // Find nearest wall intersection along the ray
    let nearest: { point: { x: number; y: number }; distance: number } | null = null;

    for (const wall of walls) {
      const segment = {
        start: { x: wall.x, y: wall.y },
        end: { x: wall.x2, y: wall.y2 }
      };

      const hit = raySegmentIntersection(drawTool.startPoint, direction, segment);
      // Skip walls at the origin (e.g., the wall we're starting from)
      if (hit && hit.distance > 1 && (nearest === null || hit.distance < nearest.distance)) {
        nearest = { point: hit.point, distance: hit.distance };
      }
    }

    if (!nearest) return;

    const newWall = createWallPath([drawTool.startPoint, nearest.point]);
    setShapes((prev) => [...prev, newWall]);
    // Clear draw state (startPoint) and preview
    handleDrawEnd(drawTool.startPoint, nearest.point, "star");
    setPreviewShape(null);
  };

  const extendDeps = [
    shortcuts.global.wallExtend,
    shapes,
    activeTool,
    drawTool.startPoint
  ];

  // Listen for both 'e' and 'shift+e' so extend works while Shift is held for angle snapping
  useHotkeys(
    shortcuts.global.wallExtend,
    extendWall,
    shortcutOptions,
    extendDeps
  );

  useHotkeys(
    `shift+${shortcuts.global.wallExtend}`,
    extendWall,
    shortcutOptions,
    extendDeps
  );
};
